import {
  BrowserRouter as Router,
  Navigate,
  Route,
  Routes,
} from "react-router-dom";
import { useEffect } from "react";
import { Paths } from "./Paths";
import useLoadCache from "../hook/useLoadCache";
import LoginPage from "../pages/LoginPage";
import HomePage from "../pages/HomePage";
import useCheckAuthentication from "../hook/useCheckAuthentication";
import ProgressBar from "../components/common/ProgressBar";
import SideBar from "../components/common/SideBar";
import CarsPage from "../pages/CarsPage";
import UsersPage from "../pages/UsersPage";
import ReportsPage from "../pages/ReportsPage";
import StaffPage from "../pages/StaffPage";
import ClicksPage from "../pages/ClicksPage";
import CarPage from "pages/CarPage";
import MessagesScreen from "pages/MessagesScreen";
import UserPage from "pages/UserPage";
import OneStaffPage from "pages/OneStaffPage";
import CreateStaffPage from "pages/CreateStaffPage";
import { useSelector } from "react-redux";
import { RootState } from "app/store";
import MoneyRequestPage from "pages/MoneyRequestPage";
import CarRequestPage from "pages/CarRequestPage";
import ShareRedirectPage from "../pages/ShareRedirectPage";
import NotificationsPage from "pages/NotificationsPage";
import AppUpdatePage from "pages/AppUpdatePage";
import DealerLoginPage from "pages/DealerLoginPage";
import DealerDealsPage from "pages/DealerDealsPage";
import DealerDealPostPage from "pages/DealerDealPostPage";
import DealerInventoryPage from "pages/DealerInventoryPage";
import DealerMarketplacePage from "pages/DealerMarketplacePage";
import SparePartsPage from "pages/SparePartsPage";
import ShortVideosPage from "pages/ShortVideosPage";
import QuickCapturePage from "pages/QuickCapturePage";
import CarImportPage from "pages/CarImportPage";
import ValuationsPage from "pages/ValuationsPage";
import DistributionPage from "pages/DistributionPage";
import useNotifications from "hook/useNotifications";
import { DashboardTranslationBridge } from "components/common/DashboardLocalization";

function AppRouter() {
  useLoadCache();

  return (
    <Router>
      <AppRoutes />
    </Router>
  );
}

function AppRoutes() {
  const { userLoaded } = useSelector((state: RootState) => state.user);
  useCheckAuthentication();
  const host = window.location.hostname.toLowerCase();
  const isLocalPreviewHost = host === "localhost" || host === "127.0.0.1";
  const isDealerHost = host.startsWith("dealer.");
  const isLocalDealerPreview =
    isLocalPreviewHost && window.location.pathname.startsWith("/dealer");
  const isDealerMode = isDealerHost || isLocalDealerPreview;
  const dealerBasePath = isLocalDealerPreview ? "/dealer" : "";
  const dealerLoginPath = `${dealerBasePath}${Paths.dealerLogin}`;
  const dealerInventoryPath = `${dealerBasePath}${Paths.dealerInventory}`;
  const dealerDealPostPath = `${dealerBasePath}${Paths.dealerDealPost}`;

  useEffect(() => {
    if (isDealerMode) applyDealerHead();
  }, [isDealerMode]);

  if (isDealerMode) {
    return (
      <div className="w-full relative min-h-screen">
        <ProgressBar />
        <Routes>
          <Route path={dealerLoginPath} Component={DealerLoginPage} />
          <Route path={dealerDealPostPath} Component={DealerDealPostPage} />
          <Route path={dealerInventoryPath} Component={DealerInventoryPage} />
          <Route
            path={dealerBasePath || Paths.home}
            Component={DealerDealsPage}
          />
          <Route
            path="*"
            element={<Navigate to={dealerBasePath || Paths.home} replace />}
          />
        </Routes>
      </div>
    );
  }

  return (
    <div className={ClassName.appContainer}>
      <DashboardTranslationBridge />
      <ProgressBar />
      <Routes>
        <Route path="/share/:id" Component={ShareRedirectPage} />
        <Route
          path={Paths.legacyDealerLogin}
          element={<DealerHostRedirect path={Paths.dealerLogin} />}
        />
        <Route
          path={Paths.legacyDealerInventory}
          element={<DealerHostRedirect path={Paths.dealerInventory} />}
        />
        {userLoaded && <Route path={Paths.login} Component={LoginPage} />}
        {userLoaded && <Route path={"*"} Component={LoggedInRoutes} />}
      </Routes>
    </div>
  );
}

function setHeadLink(rel: string, href: string, type?: string) {
  let link = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!link) {
    link = document.createElement("link");
    link.rel = rel;
    document.head.appendChild(link);
  }

  link.href = href;
  if (type) {
    link.type = type;
  } else {
    link.removeAttribute("type");
  }
}

function applyDealerHead() {
  document.title = "BossCar Deals";
  setHeadLink("icon", "/dealer-favicon.ico", "image/x-icon");
  setHeadLink("shortcut icon", "/dealer-favicon.ico", "image/x-icon");
  setHeadLink("apple-touch-icon", "/dealer-apple-touch-icon.png", "image/png");
  setHeadLink("manifest", "/dealer-manifest.json", "application/manifest+json");
}

function DealerHostRedirect({ path }: { path: string }) {
  const dealerOrigin = "https://dealer.bosscarnetwork.com";
  const host = window.location.hostname.toLowerCase();

  if (host === "localhost" || host === "127.0.0.1") {
    return <Navigate to={`/dealer${path}`} replace />;
  }

  window.location.replace(`${dealerOrigin}${path}`);
  return null;
}

function LoggedInRoutes() {
  return (
    <div className={ClassName.loggedInContainer}>
      <NotificationServices />
      <SideBar />
      <Routes>
        <Route path={Paths.home} Component={HomePage} />
        <Route path={Paths.cars} Component={CarsPage} />
        <Route path={Paths.car} Component={CarPage} />
        <Route path={Paths.users} Component={UsersPage} />
        <Route path={Paths.user} Component={UserPage} />
        <Route path={Paths.messages} Component={MessagesScreen} />
        <Route path={Paths.reports} Component={ReportsPage} />
        <Route path={Paths.staff} Component={StaffPage} />
        <Route path={Paths.oneStaff} Component={OneStaffPage} />
        <Route path={Paths.createStaff} Component={CreateStaffPage} />
        <Route path={Paths.clicks} Component={ClicksPage} />
        <Route path={Paths.money} Component={MoneyRequestPage} />
        <Route path={Paths.carRequest} Component={CarRequestPage} />
        <Route path={Paths.spareParts} Component={SparePartsPage} />
        <Route path={Paths.shortVideos} Component={ShortVideosPage} />
        <Route path={Paths.notifications} Component={NotificationsPage} />
        <Route path={Paths.appUpdate} Component={AppUpdatePage} />
        <Route
          path={Paths.dealerMarketplace}
          Component={DealerMarketplacePage}
        />
        <Route path={Paths.quickCapture} Component={QuickCapturePage} />
        <Route path={Paths.carImport} Component={CarImportPage} />
        <Route path={Paths.valuations} Component={ValuationsPage} />
        <Route path={Paths.distribution} Component={DistributionPage} />
      </Routes>
    </div>
  );
}

function NotificationServices() {
  useNotifications();
  return null;
}

export default AppRouter;

const ClassName = {
  appContainer: "relative min-h-screen w-full",
  loggedInContainer:
    "relative flex min-h-screen w-full min-w-0 flex-row bg-slate-50 xl:pl-72",
};
